import React from 'react';
import styled from '@emotion/styled';
import { CTA } from '../../../components/CTA';
import { MarkAsFavorite } from '../../../components/MarkAsFavorite';
import { PriceString } from '../../../components/PriceString';
import { formatUrgencyLabels } from '../../../helpers/cruiseSearchHelper';
import { replacePlaceholders, __ } from '../../../helpers/TranslationService';
import { getTranslationKey, LabelBox } from '../../../components/LabelBox/LabelBox';
import { UrgencyLabel } from './UrgencyLabel';
import { hasHotelServiceCharges } from '../../../helpers/hotelServiceChargesHelper';

const shouldShowDiscount = (props: any): boolean =>
    props.selectedSail.price > 0 &&
    props.selectedSail.discount > 0 &&
    props.selectedSail.discount >= props.discountThreshold;

const CheckMarkIcon = styled.li`
    &::before {
        color: ${({ theme }) => theme.icon.brandColor2};
    }
`;

export const SearchResultItemMiddleDesktopSection = (props: any) => {
    const {
        cruise,
        hideFavoritesIcon,
        isFavorite,
        addToFavorites,
        removeFromFavorites,
        searchPageType,
        selectedSail,
    } = props;
    const {
        shipImage,
        routeImageLarge,
        routeMapImage,
        isPackageCruise,
        urgencyLabel,
        obc,
        includesFlight,
        ugcLabels,
    } = cruise;
    let headerImage =
        searchPageType === 'map' || searchPageType === 'mapWithNoUGC'
            ? routeMapImage
            : searchPageType === 'ship'
            ? shipImage
            : routeImageLarge;
    const hasRouteImage = Boolean(routeMapImage);

    const formattedUrgencyLabels = formatUrgencyLabels(urgencyLabel, obc, isPackageCruise);
    const priceProps = {
        currency: selectedSail.currency,
        price: selectedSail.price,
    };

    const ugcLabelsFiltered = ugcLabels?.filter(
        (textLabel) =>
            textLabel.label !== 'bestseller' &&
            textLabel.label !== 'highDemand' &&
            __(getTranslationKey(textLabel.label), 'dreamlines') !==
                getTranslationKey(textLabel.label),
    );

    const includesHotelServiceCharges = hasHotelServiceCharges(
        props.portalId,
        cruise.companyTitle,
        cruise.shipTitle,
        cruise.earliestDeparture,
    );

    const allowedMarketFlags = ['dreamlines.de'].includes(props.portalId);

    const isPackageDreamlines = cruise.isPackageCruise;

    const isFlightOrPackage = includesFlight || isPackageDreamlines;

    const showBanner = allowedMarketFlags && isFlightOrPackage;

    const hotelChargesAsPlainText =
        includesHotelServiceCharges &&
        ((allowedMarketFlags && !isFlightOrPackage) || !allowedMarketFlags);

    return (
        <>
            <div className="searchResultItemMiddleDesktopSection">
                <div className="searchResultItemMiddleDesktopSection__galleryContainer">
                    <img
                        src={headerImage || shipImage}
                        alt={cruise.companyTitle}
                        className={hasRouteImage && 'routeImage'}
                    />
                    {!hideFavoritesIcon && (
                        <MarkAsFavorite
                            cruiseNid={cruise.cruiseNid}
                            isFavorite={isFavorite}
                            addToFavorites={addToFavorites}
                            removeFromFavorites={removeFromFavorites}
                        />
                    )}
                </div>
                <div className="searchResultItemMiddleDesktopSection__infoArea">
                    <div className="searchResultItemMiddleDesktopSection__topInfo">
                        <div className="searchResultItemMiddleDesktopSection__info">
                            <ul>
                                <li className="icon-shipv2">{cruise.shipTitle}</li>
                                <li className="icon-globe">
                                    {cruise.areaTitle.split(',').join(', ')}
                                </li>

                                {cruise?.earliestDeparture && (
                                    <li className="icon-calendar">{cruise.dateRange}</li>
                                )}

                                {cruise?.icons
                                    ?.filter((item) => item)
                                    .map((item) => (
                                        <CheckMarkIcon
                                            key={item?.icon}
                                            className="icon-checkMark icon"
                                        >
                                            {__(item?.text, 'dreamlines')}
                                        </CheckMarkIcon>
                                    ))}

                                {showBanner && includesHotelServiceCharges && (
                                    <CheckMarkIcon className="icon-checkMark icon">
                                        <div className="hotelServiceChargesLabel ">
                                            {__('incl. hotel service charges', 'dreamlines')}
                                        </div>
                                    </CheckMarkIcon>
                                )}
                            </ul>
                        </div>
                        <div className="searchResultItemMiddleDesktopSection__price-labels">
                            <img
                                src={cruise.companyLogo}
                                alt={cruise.companyTitle}
                                title={cruise.companyTitle}
                                className="companyLogo"
                            />

                            {formattedUrgencyLabels.length > 0 &&
                                formattedUrgencyLabels.map((urgencyLabel) => {
                                    return (
                                        <UrgencyLabel
                                            key={urgencyLabel.type}
                                            label={urgencyLabel}
                                            isNewDesign={true}
                                        />
                                    );
                                })}

                            <div className="priceCTAWrapper">
                                <div className="priceWrapper">
                                    {shouldShowDiscount(props) && (
                                        <span className="discount">
                                            {replacePlaceholders(
                                                __('upToXDiscount', 'dreamlines'),
                                                [`-${selectedSail.discount}%`],
                                            )}
                                        </span>
                                    )}
                                    {selectedSail.price > 0 ? (
                                        <>
                                            <div>
                                                <span className="priceFrom">
                                                    {__('from', 'dreamlines')}
                                                </span>
                                                <div className="priceContainer">
                                                    <span className="priceString">
                                                        <PriceString {...priceProps} />
                                                    </span>
                                                </div>
                                                <span className="pricePerPerson">
                                                    {__('p.P.', 'dreamlines')}
                                                </span>
                                            </div>

                                            <div className="priceIncludingTextSpan">
                                                {showBanner && (
                                                    <div className="includingFlightButton">
                                                        {includesFlight
                                                            ? isPackageDreamlines
                                                                ? __(
                                                                      'package incl. flight',
                                                                      'dreamlines',
                                                                  )
                                                                : __(
                                                                      'cruise incl. flight',
                                                                      'dreamlines',
                                                                  )
                                                            : isPackageDreamlines &&
                                                              __(
                                                                  'package with extras',
                                                                  'dreamlines',
                                                              )}
                                                    </div>
                                                )}
                                            </div>
                                            {hotelChargesAsPlainText && (
                                                <div className="hotelServiceChargesLabel">
                                                    {__(
                                                        'incl. hotel service charges',
                                                        'dreamlines',
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="priceString">
                                            {__('on request', 'dreamlines')}
                                        </div>
                                    )}
                                </div>

                                <CTA className="CTA">
                                    <span>{__('View Offer', 'dreamlines')}</span>
                                </CTA>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {ugcLabelsFiltered?.length > 0 && (
                <div className="searchResultItemMiddleDesktopSection__ugcLabels">
                    <span className="text">
                        {__('Thats what our customers say', 'dreamlines')}:
                    </span>
                    <ul className="labelsWrapper">
                        {ugcLabelsFiltered?.map((textLabel, index) => {
                            return (
                                <li key={index}>
                                    <LabelBox
                                        type={textLabel.label}
                                        label={__(getTranslationKey(textLabel.label), 'dreamlines')}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </>
    );
};
